import { useEffect, useMemo, useState } from "react";
import { Popover as PopoverPrimitive } from "radix-ui";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useOnboardingStore } from "@/stores/useOnboardingStore";
import { isOpBarCalloutSeen, markOpBarCalloutSeen } from "@/lib/opBarCallout";

const BAR_SELECTOR = '[data-tour-target="operation-bar"]';
const POLL_INTERVAL_MS = 300;

/**
 * Just-in-time coachmark teaching the OperationBar (bottom progress bar) the
 * first time it appears for a user. The bar is transient (`OperationBar`
 * returns null when no op is running), so "bar element present in DOM" is
 * equivalent to "an operation is active" — we detect that rather than
 * re-aggregating the ~10 operation stores.
 *
 * Latches once when: not-yet-seen AND the bar is present AND no other guidance
 * surface is active. Claims the `callout` surface while visible so it doesn't
 * stack with the SuggestionCard. Learn-once per user.
 */
export function OperationBarCallout() {
  const { data: currentUser } = useCurrentUser();
  const authId = currentUser?.auth_id;

  const [seen, setSeen] = useState(() => (authId ? isOpBarCalloutSeen(authId) : true));
  const [shown, setShown] = useState(false);
  const [barEl, setBarEl] = useState<HTMLElement | null>(null);

  // Re-sync `seen` if the user resolves after mount.
  useEffect(() => {
    if (authId) setSeen(isOpBarCalloutSeen(authId));
  }, [authId]);

  // Poll for the bar; latch when eligible. Reads activeSurface via getState()
  // (non-reactive) so the latch check happens at fire time, before we claim.
  useEffect(() => {
    if (!authId || seen || shown) return;

    function check() {
      const el = document.querySelector(BAR_SELECTOR) as HTMLElement | null;
      if (!el) return;
      if (useOnboardingStore.getState().activeSurface !== null) return; // wait for tour/etc.
      setBarEl(el);
      setShown(true);
      useOnboardingStore.getState().claim("callout");
    }

    check(); // immediate, so we don't always wait a full interval
    const id = window.setInterval(check, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [authId, seen, shown]);

  // Release the surface if we unmount while still showing.
  useEffect(() => {
    return () => {
      if (useOnboardingStore.getState().activeSurface === "callout") {
        useOnboardingStore.getState().release("callout");
      }
    };
  }, []);

  const virtualRef = useMemo(() => (barEl ? { current: barEl } : null), [barEl]);

  function dismiss() {
    if (authId) markOpBarCalloutSeen(authId);
    setSeen(true);
    setShown(false);
    useOnboardingStore.getState().release("callout");
  }

  if (!shown || !virtualRef) return null;

  return (
    <PopoverPrimitive.Root open>
      <PopoverPrimitive.Anchor virtualRef={virtualRef} />
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="top"
          sideOffset={12}
          align="center"
          avoidCollisions
          collisionPadding={16}
          className="z-[70] w-[min(360px,90vw)] rounded-md border bg-popover p-4 text-popover-foreground shadow-md"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="text-sm font-semibold mb-1">Your work runs here</div>
          <div className="text-sm text-muted-foreground mb-4">
            Trawling, scoring, and other long tasks show progress at the bottom.
            Click a pill to expand for detail — or to cancel.
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={dismiss}
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Got it
            </button>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
